import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDistanceToNow, format, isToday, subDays, isAfter } from "date-fns";

// Theme & Navigation
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";
import { Path } from "../../../../static/Constants";
import { CommonBreadcrumbs } from "../../../Components/ui";

// Blockchain Services
import web3 from "../../../Service/web3";
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";

// Subcomponents
import {
  NotificationHero,
  NotificationFilters,
  NotificationCard,
  NotificationDetailModal,
  NotificationEmptyState,
  NotificationListSkeleton,
  NotificationHeroSkeleton,
  getNotificationConfig,
} from "./components";

import "./NotificationScreenStyle.css";

const ITEMS_PER_PAGE = 10;

const formatTimeAgo = (dateTimeString) => {
  if (!dateTimeString) return "Recently";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Recently";
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "Recently";
  }
};

const formatExactDate = (dateTimeString) => {
  if (!dateTimeString) return "-";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "-";
    return format(date, "MMM d, yyyy • h:mm a");
  } catch {
    return "-";
  }
};

const getNotificationUniqueId = (notification, index) => {
  if (!notification) return String(index);
  return (
    notification._id ||
    notification.id ||
    `${notification.type}-${notification.genId || ""}-${notification.createdOn || index}`
  );
};

function NotificationScreen() {
  const history = useHistory();
  const apiUrl = process.env.REACT_APP_API_KEY;

  // Account State
  const [accountAddress, setAccountAddress] = useState("");
  const [adminAccount, setAdminAccount] = useState(false);
  const [creatorAccount, setCreatorAccount] = useState(false);

  // Cached initial data from localStorage if present
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem("notifications");
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {}
    return [];
  });

  const [loading, setLoading] = useState(() => {
    try {
      const saved = localStorage.getItem("notifications");
      if (saved) {
        const parsed = JSON.parse(saved);
        return !Array.isArray(parsed) || parsed.length === 0;
      }
    } catch {}
    return true;
  });

  const [refreshing, setRefreshing] = useState(false);

  // Read Notifications state from localStorage
  const [readIds, setReadIds] = useState(() => {
    try {
      const savedRead = localStorage.getItem("autobet_user_read_notifications");
      if (savedRead) {
        const parsed = JSON.parse(savedRead);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch {}
    return new Set();
  });

  // Filters and Search State
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [timeRange, setTimeRange] = useState("all"); // "all" | "today" | "7days" | "30days"
  const [sortBy, setSortBy] = useState("newest"); // "newest" | "oldest"
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  // Detail Modal State
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Sync read notifications to localStorage
  const markAsRead = useCallback((notificationId) => {
    if (!notificationId) return;
    setReadIds((prev) => {
      if (prev.has(notificationId)) return prev;
      const next = new Set(prev);
      next.add(notificationId);
      try {
        localStorage.setItem(
          "autobet_user_read_notifications",
          JSON.stringify(Array.from(next))
        );
      } catch {}
      return next;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    const allIds = notifications.map((n, idx) => getNotificationUniqueId(n, idx));
    setReadIds((prev) => {
      const next = new Set([...prev, ...allIds]);
      try {
        localStorage.setItem(
          "autobet_user_read_notifications",
          JSON.stringify(Array.from(next))
        );
      } catch {}
      return next;
    });
    toast.success("All notifications marked as read", {
      position: "top-right",
      autoClose: 2000,
    });
  }, [notifications]);

  // Fetch Notifications from API with watchdog and abort controller
  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setNotifications((current) => {
          if (!current || current.length === 0) {
            setLoading(true);
          }
          return current;
        });
      }

      // Safety watchdog: Guarantees loading is never stuck
      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 5000);

      const controller = new AbortController();
      const abortTimeout = setTimeout(() => controller.abort(), 6000);

      try {
        let userAcc = accountAddress;
        if (!userAcc && web3 && web3.eth) {
          try {
            const accounts = await web3.eth.getAccounts();
            if (accounts && accounts.length > 0) {
              userAcc = accounts[0];
              setAccountAddress(userAcc);
            }
          } catch (accErr) {
            console.warn("Could not retrieve web3 accounts:", accErr);
          }
        }

        const headers = userAcc ? { Authorization: userAcc } : {};

        const response = await fetch(
          `${apiUrl}/notifications?limit=100&skip=0`,
          {
            method: "GET",
            headers,
            signal: controller.signal,
          }
        );

        clearTimeout(abortTimeout);
        clearTimeout(watchdog);

        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data.result)
            ? data.result
            : Array.isArray(data)
            ? data
            : [];
          setNotifications(items);
          localStorage.setItem("notifications", JSON.stringify(items));

          // Check admin & creator roles if account is connected
          if (userAcc) {
            try {
              if (cinstance?.methods?.admin) {
                const adminAdd = await cinstance.methods.admin().call();
                if (userAcc.toLowerCase() === adminAdd?.toLowerCase()) {
                  setAdminAccount(true);
                }
              }

              if (usercinstance?.methods?.ownerId) {
                const res = await usercinstance.methods.ownerId().call();
                const totalOwners = Number(res) || 0;
                for (let i = totalOwners - 1; i > 0; i--) {
                  const creatorAdd = await usercinstance.methods
                    .organisationbyid(i)
                    .call();
                  if (userAcc.toLowerCase() === creatorAdd?.toLowerCase()) {
                    setCreatorAccount(true);
                    break;
                  }
                }
              }
            } catch (roleErr) {
              console.warn("Could not determine user contract roles:", roleErr);
            }
          }
        } else {
          console.error("Error response fetching notifications:", response.statusText);
        }
      } catch (error) {
        console.warn("Error fetching notifications:", error);
      } finally {
        clearTimeout(watchdog);
        clearTimeout(abortTimeout);
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accountAddress, apiUrl]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Destination Action Routing
  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification) return;
      const uniqueId = getNotificationUniqueId(notification, 0);
      markAsRead(uniqueId);

      const type = notification.type;
      const genId = notification.genId;
      const lotteryName = notification.lotteryName;

      switch (type) {
        case "LotteryCreated":
        case "LotteryCreatedAdmin":
        case "RolloverHappened":
        case "RolloverHappened'":
          if (lotteryName === "Missile") {
            history.push(genId ? `/missilelottery/${genId}` : "/missilelottery");
          } else if (lotteryName === "Mine") {
            history.push(genId ? `/minelottery/${genId}` : "/minelottery");
          } else if (lotteryName === "MRL") {
            history.push(genId ? `/lottery/${genId}` : "/lottery");
          } else {
            history.push("/spinnerlottery");
          }
          break;

        case "LotteryResult":
        case "SpinLotteryResult":
        case "LotteryWinner":
          if (adminAccount && genId) {
            history.push(`/admin/lotterydetails/${genId}`);
          } else if (
            creatorAccount &&
            genId &&
            accountAddress &&
            notification.owner &&
            accountAddress.toLowerCase() === notification.owner.toLowerCase()
          ) {
            history.push(`/creator/lotterydetails/${genId}`);
          } else {
            history.push(`/lottery-result`);
          }
          break;

        case "GiftCardCreated":
          if (genId) {
            history.push(`/giftcard/${genId}`);
          } else {
            history.push(`/allgiftcards`);
          }
          break;

        case "GiftCardPurchased":
          if (adminAccount && genId) {
            history.push(`/admin/giftcarddetails/${genId}`);
          } else {
            history.push(`/mygiftcard`);
          }
          break;

        case "OrganisationAdded":
          if (adminAccount) {
            history.push(`/admin/creatorslist`);
          } else {
            history.push(`/creator`);
          }
          break;

        case "LotteryBought":
          if (adminAccount && genId) {
            history.push(`/admin/lotterydetails/${genId}`);
          } else if (
            creatorAccount &&
            genId &&
            accountAddress &&
            notification.owner &&
            accountAddress.toLowerCase() === notification.owner.toLowerCase()
          ) {
            history.push(`/creator/lotterydetails/${genId}`);
          } else if (
            accountAddress &&
            notification.user &&
            accountAddress.toLowerCase() === notification.user.toLowerCase()
          ) {
            history.push(`/profile`);
          } else {
            history.push(`/lottery-result`);
          }
          break;

        case "WinnerPaid":
          if (
            accountAddress &&
            notification.user &&
            accountAddress.toLowerCase() === notification.user.toLowerCase()
          ) {
            history.push(`/profile`);
          } else {
            history.push(`/lottery-result`);
          }
          break;

        case "PartnerCreated":
          if (adminAccount) {
            history.push(`/admin/partnerslist`);
          } else {
            history.push(`/#partners`);
          }
          break;

        case "PartnerPaid":
          history.push(`/creator`);
          break;

        case "PlatformUpdate":
          history.push(`/news`);
          break;

        default:
          if (genId) {
            setSelectedNotification(notification);
            setDetailModalOpen(true);
          }
          break;
      }
    },
    [accountAddress, adminAccount, creatorAccount, history, markAsRead]
  );

  const handleOpenDetails = useCallback(
    (notification) => {
      setSelectedNotification(notification);
      setDetailModalOpen(true);
      const uniqueId = getNotificationUniqueId(notification, 0);
      markAsRead(uniqueId);
    },
    [markAsRead]
  );

  // Compute category counts for tab badges
  const countsByTab = useMemo(() => {
    const counts = {
      all: notifications.length,
      lottery: 0,
      winner: 0,
      giftcard: 0,
      partner: 0,
      platform: 0,
    };

    notifications.forEach((item) => {
      const conf = getNotificationConfig(item.type);
      const cat = conf.category;
      if (counts[cat] !== undefined) {
        counts[cat] += 1;
      } else {
        counts.platform += 1;
      }
    });

    return counts;
  }, [notifications]);

  // Compute unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n, idx) => {
      const id = getNotificationUniqueId(n, idx);
      return !readIds.has(id);
    }).length;
  }, [notifications, readIds]);

  // Filter and Sort Notifications
  const filteredNotifications = useMemo(() => {
    let result = [...notifications];

    // 1. Tab / Category Filter
    if (activeTab !== "all") {
      result = result.filter((item) => {
        const conf = getNotificationConfig(item.type);
        return conf.category === activeTab;
      });
    }

    // 2. Unread Only Filter
    if (unreadOnly) {
      result = result.filter((item, idx) => {
        const id = getNotificationUniqueId(item, idx);
        return !readIds.has(id);
      });
    }

    // 3. Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const titleMatch = (item.title || "").toLowerCase().includes(q);
        const contentMatch = (item.content || "").toLowerCase().includes(q);
        const typeMatch = (item.type || "").toLowerCase().includes(q);
        const gameMatch = (item.lotteryName || "").toLowerCase().includes(q);
        const genIdMatch = String(item.genId || "").toLowerCase().includes(q);
        return titleMatch || contentMatch || typeMatch || gameMatch || genIdMatch;
      });
    }

    // 4. Time Range Filter
    if (timeRange !== "all") {
      result = result.filter((item) => {
        if (!item.createdOn) return false;
        try {
          const itemDate = new Date(item.createdOn);
          if (isNaN(itemDate.getTime())) return true;
          if (timeRange === "today") {
            return isToday(itemDate);
          }
          if (timeRange === "7days") {
            return isAfter(itemDate, subDays(new Date(), 7));
          }
          if (timeRange === "30days") {
            return isAfter(itemDate, subDays(new Date(), 30));
          }
        } catch {
          return true;
        }
        return true;
      });
    }

    // 5. Sorting
    result.sort((a, b) => {
      const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
      const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      if (sortBy === "oldest") {
        return dateA - dateB;
      }
      return dateB - dateA;
    });

    return result;
  }, [notifications, activeTab, unreadOnly, searchQuery, timeRange, sortBy, readIds]);

  // Reset all active filters
  const handleResetFilters = useCallback(() => {
    setActiveTab("all");
    setSearchQuery("");
    setTimeRange("all");
    setSortBy("newest");
    setUnreadOnly(false);
    setPage(1);
  }, []);

  const hasActiveFilters =
    activeTab !== "all" ||
    searchQuery.trim().length > 0 ||
    timeRange !== "all" ||
    unreadOnly ||
    sortBy !== "newest";

  // Pagination calculation
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE) || 1;
  const paginatedNotifications = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredNotifications.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredNotifications, page]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 180, behavior: "smooth" });
  };

  const showingStart = filteredNotifications.length === 0 ? 0 : (page - 1) * ITEMS_PER_PAGE + 1;
  const showingEnd = Math.min(page * ITEMS_PER_PAGE, filteredNotifications.length);

  return (
    <div id="notifications" className="notification-screen-container">
      <NavBar />
      <ToastContainer />

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 6,
          flexGrow: 1,
        }}
      >
        {/* Breadcrumb Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "Notifications" },
          ]}
        />

        {/* Hero Section */}
        {loading && notifications.length === 0 ? (
          <NotificationHeroSkeleton />
        ) : (
          <NotificationHero
            totalCount={notifications.length}
            unreadCount={unreadCount}
            lotteryCount={countsByTab.lottery}
            winnerCount={countsByTab.winner}
            onMarkAllAsRead={markAllAsRead}
            onRefresh={() => fetchData(true)}
            refreshing={refreshing}
          />
        )}

        {/* Filters & Search Bar */}
        <NotificationFilters
          activeTab={activeTab}
          onTabChange={(tabId) => {
            setActiveTab(tabId);
            setPage(1);
          }}
          searchQuery={searchQuery}
          onSearchChange={(val) => {
            setSearchQuery(val);
            setPage(1);
          }}
          timeRange={timeRange}
          onTimeRangeChange={(val) => {
            setTimeRange(val);
            setPage(1);
          }}
          sortBy={sortBy}
          onSortChange={setSortBy}
          unreadOnly={unreadOnly}
          onUnreadOnlyToggle={() => {
            setUnreadOnly((prev) => !prev);
            setPage(1);
          }}
          countsByTab={countsByTab}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={handleResetFilters}
        />

        {/* Notifications List / Skeletons / Empty State */}
        <Box className="notification-content-wrap">
          {loading && notifications.length === 0 ? (
            <NotificationListSkeleton count={6} />
          ) : paginatedNotifications.length > 0 ? (
            <Stack spacing={2}>
              {paginatedNotifications.map((notification, index) => {
                const uniqueId = getNotificationUniqueId(notification, index);
                const isUnread = !readIds.has(uniqueId);

                return (
                  <NotificationCard
                    key={uniqueId}
                    notification={notification}
                    isUnread={isUnread}
                    onClick={() => handleNotificationClick(notification)}
                    onOpenDetails={() => handleOpenDetails(notification)}
                    formatTimeAgo={formatTimeAgo}
                    formatExactDate={formatExactDate}
                  />
                );
              })}
            </Stack>
          ) : (
            <NotificationEmptyState
              hasActiveFilters={hasActiveFilters}
              isUnreadOnly={unreadOnly}
              onResetFilters={handleResetFilters}
              onRefresh={() => fetchData(true)}
            />
          )}
        </Box>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <Box className="notification-pagination-bar">
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[600],
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Showing {showingStart} – {showingEnd} of {filteredNotifications.length} alerts
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: 600,
                  borderRadius: borderRadiusPx.md,
                },
                "& .Mui-selected": {
                  bgcolor: colors.primary.main,
                  color: colors.neutral.white,
                  "&:hover": {
                    bgcolor: colors.primary.dark,
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Full Detail Modal */}
        <NotificationDetailModal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          notification={selectedNotification}
          onNavigate={handleNotificationClick}
          formatTimeAgo={formatTimeAgo}
          formatExactDate={formatExactDate}
        />
      </Container>
    </div>
  );
}

export default NotificationScreen;
